define(function(require, exports, module) {
    'use strict';

    var _ = require('underscore');
    var BaseStepModel = require('portlets/registration/registration.step.model');
    var i18n = require('i18n');
    var userValidationConfig = require('modules/user/user.validation.config');

    module.exports = BaseStepModel.extend({

        validationsMap: {
            depositLimits: 'depositLimit',
            betLimits: 'betLimit',
            lossLimits: 'lossLimit',
            email: 'registerEmail'
        },

        initialize: function(options) {
            BaseStepModel.prototype.initialize.apply(this, arguments);

            this.parseConfigToFields(options);

            this.set('genericFields', options.genericFields);

            this.userValidation = userValidationConfig();
        },

        parseConfigToFields: function(options) {
            options.genericFields.forEach(function(field) {
                if (field.imsField === 'email') {
                    field.duplicateEmailLookup = options.duplicateEmailLookup;
                }
            });
            return options;
        },

        /**
         * @override
         */
        getData: function(data) {
            var args = Array.from(arguments);

            data = this._groupDateByIMSName(data);
            data = this._setCheckBoxValues(data);

            data = this._parseData(data).imsFields;

            data = BaseStepModel.prototype.getData.apply(this, [data].concat(args.slice(1)));

            return data;
        },

        getValidationData: function(data) {
            data = this._groupDateByIMSName(data);
            data = this._setCheckBoxValues(data);

            return data;
        },

        getPlayerTags: function(data) {
            data = this._groupDateByIMSName(data);
            data = this._setCheckBoxValues(data);

            return this._parseData(data).playerTags;
        },

        _parseData: function(data, genericFields) {
            genericFields = genericFields || this.get('genericFields');

            let imsFields = {};
            let playerTags = [];
            const fixedCodePhoneFields = genericFields.filter(item => item.areaCode === 'predefined');

            fixedCodePhoneFields.forEach(field => {
                const prefix = this.get('allowedCountries').find(item => item.code === field.phoneCode)?.phoneAreaCode;

                data[field.name] = `${prefix}${data[field.name]}`;
            });

            Object.keys(data).forEach(name => {
                const areaCodeIndex = name.lastIndexOf('-areaCode');

                if (areaCodeIndex > 0) {
                    const clearName = name.substring(0, areaCodeIndex);

                    if (clearName in data) {
                        data[clearName] = `${data[name]}${data[clearName]}`;
                    }

                    delete data[name];
                }
            });

            _.mapObject(data, function(value, fieldName) {
                var field = genericFields
                    .find(function(genericField) {
                        return genericField.name === fieldName
                            || genericField.imsFieldSession === fieldName
                            || genericField.imsFieldDaily === fieldName
                            || genericField.imsFieldMonthly === fieldName
                            || genericField.imsFieldWeekly === fieldName
                            || genericField.imsFieldAction === fieldName;
                    });

                if (!field || field.isNoIMSField) {
                    return;
                }

                var imsFieldName = field.imsField;
                if (imsFieldName) {
                    imsFields[imsFieldName] = value;

                    var extraImsFields = field.extraImsFields || '';
                    extraImsFields
                        .split(',')
                        .filter(Boolean)
                        .forEach(function(extraImsFieldName) {
                            imsFields[extraImsFieldName] = value;
                        });
                } else if (field.type === 'depositLimits' || field.type === 'betLimits' || field.type === 'lossLimits') {
                    imsFields[fieldName] = value;
                }

                if (field.usePlayerTags) {
                    playerTags = playerTags.concat(this._preparePlayerTags(field.playerTags, value));
                }

                if (field.innerFields) {
                    var parsedData = this._parseData(data, _.values(field.innerFields));
                    imsFields = _.extend({}, imsFields, parsedData.imsFields);
                    playerTags = playerTags.concat(parsedData.playerTags);
                }
            }.bind(this));

            if (data.customSubscription) {
                imsFields.customSubscription = data.customSubscription;
            }

            return {
                imsFields: imsFields,
                playerTags: playerTags
            };
        },

        _preparePlayerTags: function(playerTags, value) {
            return playerTags.map(function(tag) {
                tag = _.clone(tag);

                if (tag.type === 'number') {
                    value = Number(value);
                }
                tag.value = value;

                var expirationDate = parseInt(tag.expirationDate);

                if (Number.isNaN(expirationDate)) {
                    delete tag.expirationDate;
                } else {
                    var date = new Date();
                    date.setDate(date.getDate() + expirationDate);
                    tag.expirationDate = window.Playtech.API.formatter.date(date, '%Y-%m-%d');
                }

                return tag;
            });
        },

        /**
         * @override
         */
        getValidationConfig: function() {
            var validationConfig = {
                confirmation: {
                    fn: {
                        message: i18n.t('playtech.mobile.system.tooltip.verify'),
                        fn: function($inputs, config, form, state, event, data) {
                            var value = $inputs.val().trim();
                            var inputData = $inputs.data();

                            var originValue = data[inputData.originName];

                            if (inputData.negativeTooltip) {
                                this.message = i18n.t(inputData.negativeTooltip);
                            }

                            if (originValue) {
                                originValue = originValue.trim();
                            }

                            return value === originValue;
                        }
                    }
                }
            };

            var genericValidation = _.reduce(this.get('genericFields'), function(validation, field) {
                validation[field.name] = this._getFieldValidation(field);

                if (field.innerFields) {
                    Object.keys(field.innerFields).forEach(function(key) {
                        var innerField = field.innerFields[key];
                        validation[innerField.name] = this._getFieldValidation(innerField);
                    }.bind(this));
                }

                return validation;
            }.bind(this), {});

            return _.extend(validationConfig, genericValidation);
        },

        _wrapPredefinedValFn: function(fn) {
            var parseImsFields = function(data) {
                var genericSteps = this.get('genericSteps');
                var genericFields = _.flatten(genericSteps);
                return this._parseData(data, genericFields).imsFields;
            }.bind(this);

            return function() {
                var args = Array.from(arguments);
                args[5] = parseImsFields(args[5]);

                return fn.apply(this, args);
            };
        },

        /**
         * Get validation based on field type and configuration
         * @param  {String} field
         * @return {Object}
         */
        _getFieldValidation: function(field) {
            if (!field.validation) {
                return {};
            }

            var validationName = this.validationsMap[field.type] || field.type;
            var predefinedValidation = validationName && this.userValidation[validationName];

            if (!field.validationRules?.length) {
                return predefinedValidation || {};
            }

            return _.reduce(field.validationRules, function(result, validation) {
                // addressLookup has custom logic for `required` validation
                if (validation.type === 'required' && ['addressLookup', 'addressLookupSwiss'].includes(field.type)) {
                    return result;
                }

                if (validation.type === 'regexp' && typeof validation.regexp === 'string') {
                    if (!validation.regexp.startsWith('/')) {
                        validation.regexp = new RegExp(validation.regexp);
                    } else {
                        const flags = validation.regexp.substring(validation.regexp.lastIndexOf('/') + 1);

                        validation.regexp = new RegExp(validation.regexp.slice(1, validation.regexp.length - flags.length - 1), flags);
                    }
                }

                if (validation.type === 'internal') {
                    if (!predefinedValidation) {
                        console.error('Field "' + field.name + '" of type "' + field.type
                            + '" has unknown internal validation');
                    } else {
                        var internalTypes = ['fn', 'ajax'];
                        var internalValidation = _.reduce(internalTypes, function(memo, type) {
                            if (predefinedValidation[type]) {
                                memo[type] = { type: type };

                                _.extend(
                                    memo[type],
                                    predefinedValidation[type],
                                    { fn: this._wrapPredefinedValFn(predefinedValidation[type].fn) }
                                );
                            }

                            return memo;
                        }, {}, this);

                        _.extend(result, internalValidation);
                    }
                } else {
                    result[validation.type] = validation;
                }

                return result;
            }, {}, this);
        },

        _setCheckBoxValues: function(data) {
            var customSubscription = {};

            this.get('genericFields').forEach(function(field) {
                var name = field.name;

                if (field.imsField === 'contactPreferences') {
                    var fieldValue = data[name] || field.imsValueUnchecked; // handle if checkbox unchecked
                    var channelStatus = JSON.parse(fieldValue.trim()); // 'true'/'false' in String format

                    customSubscription[name] = {
                        channel: field.contactChannelName,
                        type: field.contactChannelType,
                        preference: channelStatus
                    };

                    data.customSubscription = customSubscription;

                    delete data[name];

                } else {
                    var imsValueUnchecked = field.imsValueUnchecked;

                    // if checkbox unchecked then data is empty then need to use uncheckedValue
                    if (field.type === 'checkbox' && imsValueUnchecked && !_.has(data, name)) {
                        data[name] = imsValueUnchecked;
                    }
                }
            });

            return data;
        },

        /**
         * Transform date data to IMS format
         * @param  {Object} data
         */
        _groupDateByIMSName: function(data) {
            const genericFields = this.get('genericFields');
            const dateFieldPrefixes = ['year_', 'month_', 'day_'];

            let fieldsToRemove = [];
            const datesObjects = Object.entries(data)
                .reduce((acc, [key, value]) => {
                    const datePrefix = dateFieldPrefixes.find((prefix) => key.startsWith(prefix));

                    if (!datePrefix) {
                        return acc;
                    }

                    const dateFieldName = key.replace(datePrefix, '');

                    if (!genericFields.find((field) => field.name === dateFieldName)) {
                        return acc;
                    }

                    acc[dateFieldName] = acc[dateFieldName] || {};
                    acc[dateFieldName][datePrefix] = value;

                    fieldsToRemove.push(key);

                    return acc;
                }, {});

            const dates = Object.entries(datesObjects)
                .reduce((acc, [fieldName, dateObject]) => {
                    const hasSomeDateFieldsMissing = dateFieldPrefixes
                        .map((prefix) => parseInt(dateObject[prefix]))
                        .some((datePart) => isNaN(datePart));

                    if (hasSomeDateFieldsMissing) {
                        return acc;
                    }

                    return {
                        ...acc,
                        [fieldName]: dateFieldPrefixes.map((prefix) => dateObject[prefix]).join('-')
                    };
                }, {});

            const filteredData = Object.fromEntries(Object.entries(data)
                .filter(([fieldName]) => !fieldsToRemove.includes(fieldName)));

            return {
                ...filteredData,
                ...dates
            };
        }
    });
});
